import { clamp } from '../core/math';
import { IDLE_RPM, REDLINE_RPM } from '../physics/constants';
import type { Track } from '../track/generate';
import type { Corner } from '../track/brakingzones';
import type { Viewport } from './renderer';

export function formatTime(ms: number): string {
  if (!Number.isFinite(ms)) return '--.---';
  const total = Math.max(0, ms);
  const m = Math.floor(total / 60000);
  const s = Math.floor((total % 60000) / 1000);
  const frac = Math.floor(total % 1000);
  const ss = String(s).padStart(2, '0');
  const fff = String(frac).padStart(3, '0');
  return m > 0 ? `${m}:${ss}.${fff}` : `${s}.${fff}`;
}

export function formatDelta(ms: number): string {
  const sign = ms >= 0 ? '+' : '-';
  return sign + (Math.abs(ms) / 1000).toFixed(3);
}

export interface HudState {
  lapMs: number;
  speedKph: number;
  bestMs: number | null;
  idealMs: number;
  deltaMs: number | null;
  brake: number;
  locked: boolean;
  offTrack: boolean;
  progress: number;
  /** Completed sector splits this lap, ms. */
  sectors: number[];
  /** Best lap's sector splits for comparison, or null. */
  bestSectors: number[] | null;
  /** Null when the tyre model is off. */
  tyre: { temp: number; wear: number; label: string } | null;
  weather: string;
  wet: boolean;
  practice: boolean;
  /** Speed difference against the ghost at the same point, km/h. */
  speedDeltaKph: number | null;
  /** Next medal to chase and the time still needed, or null once at the top. */
  medalTarget: { name: string; color: string; gapMs: number } | null;
  respawns: number;
  /** 1 = pristine, 0 = wrecked. Hidden when undamaged. */
  damage: number;
  /** Live race position, Grand Prix only. 1-based; `total` includes the player. */
  race: { position: number; total: number } | null;
  /** Tucked into another kart's wake this instant, Grand Prix only. */
  drafting: boolean;
  /** This tick's lateral g, positive left, and longitudinal g, negative under
   *  braking — the live friction-circle reading, not just the replay's. */
  latG: number;
  longG: number;
  /** Direct-drive engine speed, rpm — see IDLE_RPM/REDLINE_RPM below. Revs
   *  track wheel speed exactly; there is no clutch or gearbox to slip. */
  rpm: number;
}

/** Delta beyond this reads as "gone", so the bar pins rather than rescaling. */
const DELTA_RANGE_MS = 2000;

const SECTOR_PURPLE = '#b06bff';
const SECTOR_GREEN = '#3ecf7a';
const SECTOR_YELLOW = '#e0b13a';

const FONT_MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

export class Hud {
  constructor(
    private readonly track: Track,
    private readonly corners: Corner[],
  ) {}

  draw(ctx: CanvasRenderingContext2D, v: Viewport, s: HudState, kartX: number, kartY: number): void {
    ctx.save();
    ctx.setTransform(v.dpr, 0, 0, v.dpr, 0, 0);
    const w = v.viewW;
    const h = v.viewH;

    // The header sits where the lap and best panels want to be, and on a phone
    // the touch bar sits where the speed readout does. Inset around both.
    const compact = w < 560;
    const pad = compact ? 10 : 16;
    const top = pad + (compact ? 42 : 24);
    const bottom = h - pad - (compact ? 104 : 0);
    const k = compact ? 0.74 : 1;
    const px = (n: number): string => `${Math.round(n * k)}px`;

    const boxW = Math.round(168 * k);
    const boxH = Math.round(62 * k);

    // Lap time
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillStyle = 'rgba(8,10,13,0.55)';
    ctx.fillRect(pad, top, boxW, boxH);
    ctx.fillStyle = '#9aa4b2';
    ctx.font = `600 ${px(11)} ${FONT_MONO}`;
    ctx.fillText('LAP', pad + 12 * k, top + 9 * k);
    ctx.fillStyle = '#f2f4f8';
    ctx.font = `700 ${px(30)} ${FONT_MONO}`;
    ctx.fillText(formatTime(s.lapMs), pad + 12 * k, top + 24 * k);

    if (s.race) {
      ctx.textAlign = 'right';
      ctx.fillStyle = '#ffb454';
      ctx.font = `700 ${px(13)} ${FONT_MONO}`;
      ctx.fillText(`P${s.race.position}/${s.race.total}`, pad + boxW - 12 * k, top + 9 * k);
      ctx.textAlign = 'left';
    }

    if (s.deltaMs !== null) this.drawDeltaBar(ctx, w, top, s, k);

    // Best and ideal
    ctx.textAlign = 'right';
    ctx.fillStyle = 'rgba(8,10,13,0.55)';
    ctx.fillRect(w - pad - boxW, top, boxW, boxH);
    ctx.fillStyle = '#9aa4b2';
    ctx.font = `600 ${px(11)} ${FONT_MONO}`;
    ctx.fillText('BEST', w - pad - 12 * k, top + 9 * k);
    ctx.fillStyle = '#c9a227';
    ctx.font = `700 ${px(20)} ${FONT_MONO}`;
    ctx.fillText(s.bestMs === null ? '--.---' : formatTime(s.bestMs), w - pad - 12 * k, top + 22 * k);
    ctx.fillStyle = '#6f7b8c';
    ctx.font = `600 ${px(12)} ${FONT_MONO}`;
    ctx.fillText(`ideal ${formatTime(s.idealMs)}`, w - pad - 12 * k, top + 46 * k);

    this.drawMedalTarget(ctx, w - pad - 12 * k, top + boxH + 8 * k, s, k);

    // Speed
    ctx.textAlign = 'left';
    ctx.fillStyle = '#f2f4f8';
    ctx.font = `700 ${px(44)} ${FONT_MONO}`;
    ctx.textBaseline = 'bottom';
    ctx.fillText(String(Math.round(s.speedKph)), pad + 12 * k, bottom - 26 * k);
    ctx.fillStyle = '#9aa4b2';
    ctx.font = `600 ${px(12)} ${FONT_MONO}`;
    ctx.fillText('km/h', pad + 14 * k, bottom - 8 * k);

    if (s.drafting) {
      ctx.fillStyle = SECTOR_GREEN;
      ctx.font = `700 ${px(13)} ${FONT_MONO}`;
      ctx.fillText('DRAFT', pad + 12 * k, bottom - 44 * k);
    }

    // Sectors belong to a lap. During a drill the boxes would sit there empty
    // forever, which is worse than not drawing them.
    if (!s.practice) this.drawSectors(ctx, pad, top + boxH + 8 * k, s, k);
    // A running cursor rather than fixed offsets: the tyre bar only exists
    // when that model is on, and fixed slots either overlap it or leave a gap.
    let barX = pad + 120 * k;
    const barY = bottom - 74 * k;
    this.drawBrakeBar(ctx, barX, barY, s, k);
    barX += 32 * k;
    if (s.tyre) {
      this.drawTyres(ctx, barX, barY, s.tyre, k);
      barX += 32 * k;
    }
    this.drawRpmBar(ctx, barX, barY, s, k);
    barX += 32 * k;
    this.drawGForce(ctx, barX, barY, s, k);
    barX += 32 * k;
    if (s.damage < 1) {
      this.drawDamage(ctx, barX, barY, s.damage, k);
    }
    this.drawWarnings(ctx, w, h, s, k);

    const mapSize = Math.round(132 * k);
    this.drawMinimap(ctx, w - pad - mapSize, bottom - mapSize, mapSize, kartX, kartY);

    ctx.restore();
  }

  /**
   * A bar rather than bare digits.
   *
   * Telemetry overlays use a wide colour-changing bar for exactly one reason:
   * you can read it peripherally while your eyes stay on the corner. Digits
   * have to be looked at and parsed, which is the one thing you cannot spare
   * attention for at a braking point.
   */
  private drawDeltaBar(
    ctx: CanvasRenderingContext2D,
    w: number,
    top: number,
    s: HudState,
    k: number,
  ): void {
    const delta = s.deltaMs ?? 0;
    const ahead = delta < 0;
    const barW = 200 * k;
    const barH = 34 * k;
    const x = w / 2 - barW / 2;

    ctx.fillStyle = 'rgba(8,10,13,0.62)';
    ctx.fillRect(x, top, barW, barH);

    // Fills outward from the centre: left and green when up, right and red when
    // down, so direction is legible without reading anything.
    const frac = clamp(Math.abs(delta) / DELTA_RANGE_MS, 0, 1);
    const fillW = (barW / 2) * frac;
    ctx.fillStyle = ahead ? 'rgba(62,207,122,0.55)' : 'rgba(255,90,77,0.55)';
    ctx.fillRect(ahead ? w / 2 - fillW : w / 2, top + 2, fillW, barH - 4);

    ctx.fillStyle = 'rgba(255,255,255,0.35)';
    ctx.fillRect(w / 2 - 1, top, 2, barH);

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#f2f4f8';
    ctx.font = `700 ${Math.round(19 * k)}px ${FONT_MONO}`;
    ctx.fillText(formatDelta(delta), w / 2, top + barH / 2);

    if (s.speedDeltaKph !== null && Math.abs(s.speedDeltaKph) >= 1) {
      ctx.fillStyle = s.speedDeltaKph > 0 ? SECTOR_GREEN : '#ff5a4d';
      ctx.font = `700 ${Math.round(12 * k)}px ${FONT_MONO}`;
      ctx.fillText(
        `${s.speedDeltaKph > 0 ? '+' : ''}${Math.round(s.speedDeltaKph)} km/h`,
        w / 2,
        top + barH + 10 * k,
      );
    }

    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
  }

  /** Next medal and the time still to find. The thing to actually aim at. */
  private drawMedalTarget(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    s: HudState,
    k: number,
  ): void {
    if (!s.medalTarget) return;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'top';
    ctx.fillStyle = s.medalTarget.color;
    ctx.font = `700 ${Math.round(12 * k)}px ${FONT_MONO}`;
    ctx.fillText(
      `${s.medalTarget.name.toUpperCase()} −${(s.medalTarget.gapMs / 1000).toFixed(2)}s`,
      x,
      y,
    );
    ctx.textAlign = 'left';
  }

  /**
   * Three split boxes that fill in as you pass each sector line, coloured
   * against your best: purple for a new best sector, green for up on it, yellow
   * for down. Standard timing-screen language, and it gives feedback three times
   * a lap instead of once.
   */
  private drawSectors(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    s: HudState,
    k: number,
  ): void {
    const boxW = 54 * k;
    const boxH = 24 * k;
    const gap = 4 * k;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    for (let i = 0; i < 3; i++) {
      const bx = x + i * (boxW + gap);
      const done = i < s.sectors.length;
      const split = done ? s.sectors[i] - (i > 0 ? s.sectors[i - 1] : 0) : null;

      let colour = 'rgba(8,10,13,0.55)';
      if (split !== null && s.bestSectors && i < s.bestSectors.length) {
        const bestSplit = s.bestSectors[i] - (i > 0 ? s.bestSectors[i - 1] : 0);
        colour =
          split < bestSplit * 0.999
            ? SECTOR_PURPLE
            : split < bestSplit * 1.005
              ? SECTOR_GREEN
              : SECTOR_YELLOW;
      } else if (split !== null) {
        colour = SECTOR_PURPLE; // first ever lap: every sector is a best
      }

      ctx.fillStyle = split === null ? 'rgba(8,10,13,0.4)' : colour;
      ctx.globalAlpha = split === null ? 1 : 0.85;
      ctx.fillRect(bx, y, boxW, boxH);
      ctx.globalAlpha = 1;

      ctx.fillStyle = split === null ? '#5c6675' : '#0f1114';
      ctx.font = `700 ${Math.round(12 * k)}px ${FONT_MONO}`;
      ctx.fillText(split === null ? `S${i + 1}` : formatTime(split), bx + boxW / 2, y + boxH / 2);
    }
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
  }

  /** Temperature bar plus wear. Only drawn when the tyre model is enabled. */
  private drawTyres(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    tyre: { temp: number; wear: number; label: string },
    k: number,
  ): void {
    const w = 18 * k;
    const h = 66 * k;
    ctx.fillStyle = 'rgba(8,10,13,0.55)';
    ctx.fillRect(x, y, w, h);

    // Blue when cold, green in the window, red past it.
    const t = clamp(tyre.temp, 0, 2);
    const colour = t < 1 ? `hsl(${210 - t * 90}, 70%, ${45 + t * 10}%)` : `hsl(${120 - (t - 1) * 120}, 75%, 50%)`;
    const fill = clamp(t / 2, 0, 1) * (h - 4);
    ctx.fillStyle = colour;
    ctx.fillRect(x + 2, y + h - 2 - fill, w - 4, fill);

    // Wear eats the bar from the top.
    if (tyre.wear > 0.01) {
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(x + 2, y + 2, w - 4, tyre.wear * (h - 4));
    }

    ctx.save();
    ctx.translate(x + w / 2, y + h + 12 * k);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#9aa4b2';
    ctx.font = `600 ${Math.round(10 * k)}px ${FONT_MONO}`;
    ctx.fillText('TYRE', 0, 0);
    ctx.restore();
  }

  private drawBrakeBar(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    s: HudState,
    k: number,
  ): void {
    const w = 18 * k;
    const h = 66 * k;
    ctx.fillStyle = 'rgba(8,10,13,0.55)';
    ctx.fillRect(x, y, w, h);
    const fill = clamp(s.brake, 0, 1) * (h - 4);
    ctx.fillStyle = s.locked ? '#ff5a4d' : '#ff9d3d';
    ctx.fillRect(x + 2, y + h - 2 - fill, w - 4, fill);
    ctx.save();
    ctx.translate(x + w / 2, y + h + 12 * k);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#9aa4b2';
    ctx.font = `600 ${Math.round(10 * k)}px ${FONT_MONO}`;
    ctx.fillText('BRAKE', 0, 0);
    ctx.restore();
  }

  /** Direct-drive revs — see HudState.rpm. Same bar language as brake and
   *  tyre so it reads as part of the same instrument cluster, not a bolt-on. */
  private drawRpmBar(ctx: CanvasRenderingContext2D, x: number, y: number, s: HudState, k: number): void {
    const w = 18 * k;
    const h = 66 * k;
    ctx.fillStyle = 'rgba(8,10,13,0.55)';
    ctx.fillRect(x, y, w, h);
    const frac = clamp((s.rpm - IDLE_RPM) / (REDLINE_RPM - IDLE_RPM), 0, 1);
    const fill = frac * (h - 4);
    // Amber into the last fifth of the range, the way a real shift light climbs.
    ctx.fillStyle = frac > 0.85 ? '#ff5a4d' : frac > 0.6 ? '#ff9d3d' : '#6fb7ff';
    ctx.fillRect(x + 2, y + h - 2 - fill, w - 4, fill);
    ctx.save();
    ctx.translate(x + w / 2, y + h + 12 * k);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#9aa4b2';
    ctx.font = `600 ${Math.round(10 * k)}px ${FONT_MONO}`;
    ctx.fillText('RPM', 0, 0);
    ctx.restore();
  }

  /**
   * The live half of the friction circle — see telemetry.ts's replay version
   * for the full trace. Two numbers, not a plotted point: at a braking point
   * there is no attention to spare reading a chart, only a glance at a digit.
   */
  private drawGForce(ctx: CanvasRenderingContext2D, x: number, y: number, s: HudState, k: number): void {
    const fmt = (v: number): string => `${v >= 0 ? '+' : ''}${v.toFixed(1)}`;
    ctx.save();
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillStyle = 'rgba(8,10,13,0.55)';
    ctx.fillRect(x, y, 58 * k, 66 * k);
    ctx.font = `600 ${Math.round(9 * k)}px ${FONT_MONO}`;
    ctx.fillStyle = '#9aa4b2';
    ctx.fillText('LAT G', x + 8 * k, y + 8 * k);
    ctx.font = `700 ${Math.round(15 * k)}px ${FONT_MONO}`;
    ctx.fillStyle = Math.abs(s.latG) > 1.3 ? '#e8c14a' : '#f2f4f8';
    ctx.fillText(fmt(s.latG), x + 8 * k, y + 19 * k);
    ctx.font = `600 ${Math.round(9 * k)}px ${FONT_MONO}`;
    ctx.fillStyle = '#9aa4b2';
    ctx.fillText('LONG G', x + 8 * k, y + 41 * k);
    ctx.font = `700 ${Math.round(15 * k)}px ${FONT_MONO}`;
    ctx.fillStyle = s.longG < -0.4 ? '#ff9d3d' : '#f2f4f8';
    ctx.fillText(fmt(s.longG), x + 8 * k, y + 52 * k);
    ctx.restore();
  }

  /** Red vertical integrity bar; full when pristine, empties with damage. */
  private drawDamage(ctx: CanvasRenderingContext2D, x: number, y: number, damage: number, k: number): void {
    const frac = Math.max(0, damage);
    ctx.save();
    ctx.fillStyle = 'rgba(8,10,13,0.55)';
    ctx.fillRect(x, y, 30 * k, 66 * k);
    ctx.font = `600 ${Math.round(9 * k)}px ${FONT_MONO}`;
    ctx.fillStyle = '#9aa4b2';
    ctx.fillText('DAMAGE', x + 5 * k, y + 8 * k);
    // Empty shape then the remaining integrity — red, so it reads as danger.
    ctx.strokeStyle = 'rgba(255,90,77,0.35)';
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 8 * k, y + 22 * k, 14 * k, 38 * k);
    ctx.fillStyle = frac > 0.5 ? '#ff9d3d' : '#ff5a4d';
    const barH = 38 * k * frac;
    const barY = y + 22 * k + 38 * k - barH;
    ctx.fillRect(x + 8 * k, barY, 14 * k, barH);
    ctx.restore();
  }

  private drawWarnings(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    s: HudState,
    k: number,
  ): void {
    const messages: Array<[string, string]> = [];
    // No PRACTICE caption: the drill panel already names the corner, and a
    // second label in the middle of the screen just covers the track.
    if (s.respawns > 0 && !s.practice) {
      messages.push([`RESPAWN ×${s.respawns}`, '#8b95a5']);
    }
    if (s.locked) messages.push(['LOCKED', '#ff5a4d']);
    else if (s.offTrack) messages.push(['OFF TRACK', '#e0b13a']);
    if (s.tyre && s.tyre.label !== 'OPTIMAL') messages.push([s.tyre.label, '#e0b13a']);
    if (messages.length === 0) return;

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    let y = h * 0.24;
    for (const [text, color] of messages) {
      ctx.font = `700 ${Math.round(22 * k)}px ${FONT_MONO}`;
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.9;
      ctx.fillText(text, w / 2, y);
      y += 30 * k;
    }
    ctx.globalAlpha = 1;
  }

  /** Whole-circuit overview with braking zones marked, so the layout is learnable. */
  private drawMinimap(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number,
    kartX: number,
    kartY: number,
  ): void {
    const b = this.track.bounds;
    const spanX = b.maxX - b.minX;
    const spanY = b.maxY - b.minY;
    const scale = (size - 20) / Math.max(spanX, spanY);
    const ox = x + 10 + (size - 20 - spanX * scale) / 2;
    const oy = y + 10 + (size - 20 - spanY * scale) / 2;
    const tx = (px: number): number => ox + (px - b.minX) * scale;
    const ty = (py: number): number => oy + (py - b.minY) * scale;

    ctx.fillStyle = 'rgba(8,10,13,0.6)';
    ctx.fillRect(x, y, size, size);

    const c = this.track.center;
    ctx.strokeStyle = '#5c6675';
    ctx.lineWidth = 3;
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(tx(c[0].x), ty(c[0].y));
    for (let i = 1; i < c.length; i++) ctx.lineTo(tx(c[i].x), ty(c[i].y));
    ctx.closePath();
    ctx.stroke();

    ctx.strokeStyle = '#ff8a3d';
    ctx.lineWidth = 3;
    for (const corner of this.corners) {
      ctx.beginPath();
      let i = corner.brakeIndex;
      ctx.moveTo(tx(c[i].x), ty(c[i].y));
      for (let guard = 0; guard < c.length; guard++) {
        if (i === corner.apexIndex) break;
        i = (i + 1) % c.length;
        ctx.lineTo(tx(c[i].x), ty(c[i].y));
      }
      ctx.stroke();
    }

    const start = c[this.track.startIndex];
    ctx.fillStyle = '#f2f4f8';
    ctx.fillRect(tx(start.x) - 2, ty(start.y) - 2, 4, 4);

    ctx.fillStyle = '#4fd1ff';
    ctx.beginPath();
    ctx.arc(tx(kartX), ty(kartY), 3.2, 0, Math.PI * 2);
    ctx.fill();
  }
}
